import { cn } from '@/lib/utils'

interface LogoProps {
  /**
   * Whether to show the logo in color or outline (white)
   */
  variant?: 'color' | 'outline'

  /**
   * The size of the logo in pixels
   */
  size?: number

  /**
   * Whether to include the text "scrambl" next to the logo
   */
  withText?: boolean

  /**
   * Optional additional class name
   */
  className?: string

  /**
   * Optional text class name for styling the text separately
   */
  textClassName?: string
}

/**
 * Logo component for displaying the Scrambl logo in various formats
 */
export function Logo({
  variant = 'color',
  size = 24,
  withText = false,
  className,
  textClassName,
}: LogoProps) {
  const svgSrc = variant === 'color' ? '/icon.svg' : '/logo-outline.svg'

  // Calculate a more balanced default text size based on logo size
  const getTextSize = () => {
    if (size <= 18) return 'text-xs'
    if (size <= 24) return 'text-sm'
    if (size <= 32) return 'text-base'
    if (size <= 40) return 'text-lg'
    if (size <= 48) return 'text-xl'
    return 'text-2xl'
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <div
        className="relative flex-shrink-0"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <img
          src={svgSrc}
          // With the wordmark beside it the mark is decorative; alone it
          // names the brand. Never "Scrambl Logo scrambl".
          alt={withText ? '' : 'Scrambl'}
          className="h-full w-full object-contain"
        />
      </div>

      {withText && (
        <span
          className={cn(
            'font-light tracking-tight text-foreground',
            getTextSize(),
            textClassName,
          )}
        >
          scrambl
        </span>
      )}
    </div>
  )
}
